import React, { startTransition, useEffect, useState } from "react";
import { Box, useDisclosure } from "@chakra-ui/react";
import SubNavigation from "./SubNavigation";

import { MainNavigationStyling } from "./styles";
import { useEventEmitter } from "utils/events";
import { TextField } from "@sitecore-jss/sitecore-jss-nextjs";
import { useHeaderBackground } from "utils/contexts/headerBackgroundContext";
import { useScroll } from "framer-motion";
import { MotionBox } from "utils/framer/motionbox";
import { useDetectClickOutside } from "react-detect-click-outside";

export const critical = true;

export interface MainNavigationFieldsProps {
    Id: string;
    DisplayName: string;
    Title: TextField;
    NavigationTitle: TextField;
    Href: string;
    Querystring: string;
    Children?: Array<MainNavigationFieldsProps>;
    Styles: string[];
}

export type MainNavigationProps = {
    fields: MainNavigationFieldsProps;
    params?: { [key: string]: string };
    handleClick: (event?: React.MouseEvent<HTMLElement>) => void;
    relativeLevel: number;
};

export const getNavigationText = (props: MainNavigationFieldsProps): string => {
    if (props?.NavigationTitle?.value) {
        return String(props.NavigationTitle.value);
    } else if (props.Title) {
        return String(props.Title.value);
    } else {
        return String(props.DisplayName);
    }
};

const MainNavigation = (props: MainNavigationProps) => {
    const [activeNavChildren, setActiveNavChildren] = useState<MainNavigationFieldsProps[]>([]);

    const { getDisclosureProps, isOpen, onOpen, onClose } = useDisclosure();
    const disclosureProps = { ...getDisclosureProps(), onClose };

    const eventEmitter = useEventEmitter<any>("navigation");
    const { hasFullBackground, setHasWhiteBackground, setMobileNavOpen } = useHeaderBackground();

    const motionBackgroundVariants = {
        open: { y: 0 },
        scrolled: {
            y: 0,
            duration: 0.1,
        },
        closed: { y: "-100%" },
    };

    const transitionBackground = {
        default: {
            ease: [0.165, 0.84, 0.44, 1],
            duration: 0.3,
        },
    };

    const ref = useDetectClickOutside({ onTriggered: () => onClose() });

    useEffect(() => {
        const bodyElement = document.body;

        bodyElement.classList.toggle("nav--open", isOpen);

        bodyElement.style.height = isOpen ? "100%" : "";
        bodyElement.style.overflow = isOpen ? "hidden" : "";

        setHasWhiteBackground(scrollY.get() > 100 || isOpen);
        setMobileNavOpen(isOpen);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const [scrolled, setScrolled] = useState<boolean>(false);
    const { scrollY } = useScroll();

    useEffect(() => {
        function updateOpacity() {
            startTransition(() => {
                setScrolled(scrollY.get() > 100);
                setHasWhiteBackground(scrollY.get() > 100 || isOpen);
            });
        }

        const unsubscribeY = scrollY.onChange(updateOpacity);

        return () => {
            unsubscribeY();
        };
    }, []);

    const handleFirstLevelClick = (fields: MainNavigationFieldsProps | undefined) => {
        setActiveNavChildren(fields?.Children ?? []);
        onOpen();

        eventEmitter.emit("onNavigationOpen", true);
    };

    const scolledAnimation = scrolled ? "scrolled" : "closed";

    return (
        <>
            {hasFullBackground && (
                <MotionBox
                    position="absolute"
                    top="0"
                    left="0"
                    width={"full"}
                    height={"full"}
                    bg="white"
                    zIndex={1}
                    initial={"closed"}
                    animate={isOpen ? "scrolled" : scolledAnimation}
                    variants={motionBackgroundVariants}
                    transition={transitionBackground}
                    onClick={() => onClose()}
                />
            )}

            <Box
                as="nav"
                ref={ref}
                className={`mainnav ${isOpen ? "mainnav--active" : ""}`}
                {...MainNavigationStyling}
            >
                <SubNavigation
                    navChildren={Array.from(Object.values(props.fields))}
                    activeNavChildren={activeNavChildren}
                    disclosureProps={disclosureProps}
                    isOpen={isOpen}
                    handleFirstLevelClick={handleFirstLevelClick}
                    navRef={ref}
                />
            </Box>
        </>
    );
};

export default MainNavigation;
